import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Text
} from 'components/ui';
import LoadingActivityCardWrapper from 'components/Content/BeautyInsider/BeautyInsiderModules/LoadingActivityCard/LoadingActivityCardWrapper';
import { HISTORY_SECTION_STATUS } from 'components/Content/BeautyInsider/BeautyInsiderHistory/historyConstants';

/**
 * Shared body renderer for History section states that do not have section-specific content.
 *
 * Loading, retryable error, and placeholder states use the same structure across History sections. Populated and
 * empty states remain in each owning section component because their content and calls to action differ by section.
 */
const HistorySectionStatus = ({ loadingLabel, numberOfLoadingCards = 3, retryLabel, section }) => {
    if (section.status === HISTORY_SECTION_STATUS.LOADING) {
        return (
            <Box aria-label={loadingLabel}>
                <LoadingActivityCardWrapper numberOfCards={numberOfLoadingCards} />
            </Box>
        );
    }

    if (section.status === HISTORY_SECTION_STATUS.ERROR) {
        return (
            <Box>
                <Text is='p'>{section.error?.message}</Text>
                {section.error?.retryable && section.retry && (
                    <Button
                        variant='secondary'
                        hasMinWidth={true}
                        marginTop={3}
                        onClick={section.retry}
                    >
                        {retryLabel}
                    </Button>
                )}
            </Box>
        );
    }

    if (section.status === HISTORY_SECTION_STATUS.PLACEHOLDER) {
        return (
            <Text
                is='p'
                lineHeight='tight'
                children={section.placeholder?.body}
            />
        );
    }

    return null;
};

HistorySectionStatus.propTypes = {
    loadingLabel: PropTypes.string.isRequired,
    numberOfLoadingCards: PropTypes.number,
    retryLabel: PropTypes.string.isRequired,
    section: PropTypes.shape({
        status: PropTypes.string.isRequired,
        placeholder: PropTypes.object,
        error: PropTypes.object,
        retry: PropTypes.func
    }).isRequired
};

export default wrapFunctionalComponent(HistorySectionStatus, 'HistorySectionStatus');
